<template>
  <md-toolbar
    md-elevation="0"
    class="md-transparent"
    :class="{
      'md-toolbar-absolute md-white md-fixed-top': $route.meta.navbarAbsolute,
    }"
  >
    <div class="md-toolbar-row">
      <div class="md-toolbar-section-start">
        <h3 class="md-title">{{ $route.name }}</h3>
      </div>
      <div class="md-toolbar-section-end">
        <md-button
          class="md-just-icon md-round md-simple md-toolbar-toggle"
          :class="{ toggled: $sidebar.showSidebar }"
          @click="toggleSidebar"
        >
          <span class="icon-bar"></span>
          <span class="icon-bar"></span>
          <span class="icon-bar"></span>
        </md-button>

        <div class="md-collapse">
          <span
            v-if="companyMode"
            class="company-mode-badge"
            :class="'mode-' + companyMode"
            :title="'privateCompanyMode في localStorage: ' + companyMode"
          >
            {{ companyMode === "private" ? "Private" : "Public" }}
          </span>
          <PushNotificationBanner />
          <NovuInbox />
          <md-list>
            <md-list-item @click="Logout">
              <i class="material-icons">logout</i>
              <p class="hidden-lg hidden-md">logout</p>
            </md-list-item>
          </md-list>
        </div>
      </div>
    </div>
  </md-toolbar>
</template>
<script>
import NovuInbox from "./../../../components/NovuInbox.vue";
import PushNotificationBanner from "./../../../components/PushNotificationBanner.vue";
import api from "@/services/api";
export default {
  components: { NovuInbox, PushNotificationBanner },
  data() {
    return {
      // Dev-only visibility check into a value the backend drives (see
      // global.js's fetchProfile) — "private" hides gov-only pages like
      // Courts/EndService (see the privateCompanyMode checks in
      // DashboardLayout.vue). Read once here, same as DashboardLayout.vue
      // does, since Vue can't observe a plain localStorage write.
      companyMode:
        process.env.NODE_ENV !== "production"
          ? localStorage.getItem("privateCompanyMode") === "true"
            ? "private"
            : "public"
          : null,
    };
  },
  methods: {
    async Logout() {
      // Call the backend BEFORE clearing localStorage — the request
      // interceptor in services/api.js reads the auth token from
      // localStorage, so the Authorization header would be missing after
      // the clear. The backend unregisters this device's push token as
      // part of handling the logout (see docs/Notifications.md — F5).
      // Best-effort: still log out locally even if this call fails
      // (offline, expired session, backend error) or times out.
      try {
        await api.post("/api/auth/Logout", null, { timeout: 5000 });
      } catch (error) {
        console.warn(
          "Logout API call failed, proceeding with local logout:",
          error
        );
      } finally {
        // Reset Vuex profile too, not just localStorage — otherwise it
        // keeps this user's profile in memory, and on a shared browser the
        // next login's dashboard can mount (NovuInbox, PushNotification
        // Banner) before ITS OWN fetchProfile() resolves, reading this
        // stale profile and sending the wrong subscriberId to Novu.
        this.$store.commit("global/resetProfile");
        localStorage.clear();
        this.$router.replace("/");
      }
    },
    toggleSidebar() {
      this.$sidebar.displaySidebar(!this.$sidebar.showSidebar);
    },
  },
};
</script>

<style>
.company-mode-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  margin-inline-end: 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
}

.company-mode-badge.mode-private {
  background: rgba(156, 39, 176, 0.12);
  color: #7b1fa2;
  border: 1px solid rgba(156, 39, 176, 0.35);
}

.company-mode-badge.mode-public {
  background: rgba(33, 150, 243, 0.12);
  color: #1565c0;
  border: 1px solid rgba(33, 150, 243, 0.35);
}

/* The base template hides .md-collapse entirely below 991px, which also
   hid the bell, the push banner, and logout with no mobile alternative —
   keep it visible here as a compact icon row instead.

   Two problems that come from forcing it back on:
   1) .md-toolbar-section-start (the page title) has flex:1 and should
      absorb the remaining width, with .md-toolbar-section-end (order:10)
      pinned to the far edge — that still holds, but .md-collapse itself
      isn't a flex child of the row, it's nested inside section-end, so it
      needs its own explicit sizing or it can end up collapsing oddly.
   2) The bell, the push banner and logout each come from a different
      system (Novu's own widget, our own button, a <md-list> reused as a
      single icon) with different default footprints. Giving each the same
      fixed box size is what actually makes them read as "the same size",
      not matching font-size alone. */
@media (max-width: 991px) {
  .md-toolbar-row {
    justify-content: space-between;
  }

  .md-toolbar-section-start {
    min-width: 0; /* let a long page title truncate instead of pushing icons out */
    overflow: hidden;
  }

  .md-toolbar-section-start .md-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .md-toolbar-section-end {
    flex: 0 0 auto;
  }

  .md-toolbar .md-collapse {
    display: flex !important;
    width: auto;
    flex: 0 0 auto;
    align-items: center;
    gap: 4px;
  }

  /* Same fixed box for every icon-only control in the collapsed row. */
  .md-toolbar .md-collapse > *,
  .md-toolbar .md-collapse .md-list {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* The badge is a text pill, not an icon — let it size to its content
     instead of being squeezed into the 36x36 box above. */
  .md-toolbar .md-collapse > .company-mode-badge {
    width: auto;
    height: auto;
  }

  .md-toolbar .md-collapse .md-list-item,
  .md-toolbar .md-collapse .md-list-item .md-list-item-container,
  .md-toolbar .md-collapse .md-list-item .md-list-item-content {
    width: 100%;
    height: 100%;
    min-height: auto;
    padding: 0;
    justify-content: center;
  }

  .md-toolbar .md-collapse .md-list-item .md-list-item-content p {
    display: none;
  }

  .md-toolbar .md-collapse .material-icons {
    font-size: 20px;
  }
}
</style>
